// Session helpers: keep an active user logged in by silently renewing their JWT before it expires,
// and detect expiry. Pure functions so the timing logic is unit-testable; the wiring (timers,
// fetch) lives in App.js.

const DEFAULT_LIFE_MS = 30 * 24 * 60 * 60 * 1000; // assume 30 days when a token has no `iat`
const FALLBACK_RENEW_REMAINING_MS = 7 * 24 * 60 * 60 * 1000; // no-iat tokens: renew in the last week

// Decode a JWT's payload. Returns null for a missing/garbage token. Shared so there is one
// base64 decoder rather than one per consumer (see utils/demoSession.js).
export function getTokenPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload && typeof payload === "object" ? payload : null;
  } catch {
    return null;
  }
}

// Decode a JWT's `exp`/`iat` (seconds) to epoch ms. Returns nulls for a missing/garbage token.
export function getTokenTimes(token) {
  try {
    const payload = getTokenPayload(token);
    if (!payload) return { expMs: null, iatMs: null };
    const exp = Number(payload && payload.exp) * 1000;
    const iat = Number(payload && payload.iat) * 1000;
    return {
      expMs: Number.isFinite(exp) && exp > 0 ? exp : null,
      iatMs: Number.isFinite(iat) && iat > 0 ? iat : null,
    };
  } catch {
    return { expMs: null, iatMs: null };
  }
}

export function tokenIsExpired(token, now = Date.now()) {
  const { expMs } = getTokenTimes(token);
  return !expMs || expMs <= now;
}

// Renew once the token is past the halfway point of its life (so an open app always refreshes well
// before it could lapse). Tokens without `iat` fall back to "renew in the final week".
export function shouldRenewToken(token, now = Date.now()) {
  const { expMs, iatMs } = getTokenTimes(token);
  if (!expMs || expMs <= now) return false; // expired -> log out, don't renew
  if (iatMs && iatMs < expMs) {
    return now >= iatMs + (expMs - iatMs) / 2;
  }
  return expMs - now <= FALLBACK_RENEW_REMAINING_MS;
}

// How often to re-check the session. Scales with token life (so a 3-minute test token is checked
// often and a 30-day token only every few hours), clamped to a sane range.
export function sessionCheckIntervalMs(token) {
  const { expMs, iatMs } = getTokenTimes(token);
  const life = expMs && iatMs ? expMs - iatMs : DEFAULT_LIFE_MS;
  return Math.min(Math.max(Math.floor(life / 8), 30 * 1000), 6 * 60 * 60 * 1000);
}

// True if the user interacted within `windowMs` of `now` (drives the presence heartbeat).
export function isRecentlyActive(lastActivityMs, now = Date.now(), windowMs = 5 * 60 * 1000) {
  if (!lastActivityMs) return false;
  return now - lastActivityMs <= windowMs;
}
